import React, { useRef, useState, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { ComponentLoader } from "../loader";
import {
  Column,
  Lineage as UILineage,
  ViewsCodeModalArgs,
} from "@altimateai/ui-components/lineage";
import { ApiHelper } from "@altimateai/ui-components/lineage";
import { Stack } from "@altimateai/lego";
import {
  getSnowflakeStage,
  getFinalLineageColumns,
  getFinalLineageTables,
  fetchDbtInfo,
  postColumns,
  postConnectedColumns,
  getBiDashboardById,
  altimateToAmundsenV2,
  upstreamTablesV2,
  downstreamTablesV2,
} from "@/helpers/apis";
import { columnRkToTableRk, destructColumn } from "./utils";
import { mapAssetToNode } from "@/pages/biReports/utils";
import { LineageColumn } from "./types";

interface AssetLineageProps {
  assetId: string;
  assetType?: string;
  height?: string;
  setViewCodeModalArgs?: (args: ViewsCodeModalArgs) => void;
  showViewCodeModal?: boolean;
  renderTags?: (entity: { column: Column; table_rk: string }) => React.ReactNode;
  exportFinalLineage?: boolean;
}

const AssetLineage: React.FC<AssetLineageProps> = ({
  assetId,
  assetType = "table",
  setViewCodeModalArgs,
  showViewCodeModal,
  renderTags,
  exportFinalLineage = false,
}) => {
  const [isApiHelperInitialized, setIsApiHelperInitialized] = useState(false);
  const [showLoader, setShowLoader] = useState(false);
  const edgeVisibility = useRef({ showNonSelectEdges: true, showSelectEdges: true });

  const TABLE_MAPPER = "TABLE-MAPPER-V3";

  const { data: assetData, isLoading } = useQuery({
    queryKey: [TABLE_MAPPER, assetId, assetType],
    queryFn: () => altimateToAmundsenV2({ asset_rk: assetId, asset_type: assetType }),
  });

  // Handle successful data fetch (replaces onSuccess callback removed in TanStack Query v5)
  useEffect(() => {
    if (!assetData) return;

    const t = assetData;
    const renderNode = {
      aiEnabled: true,
      showCodeModal: showViewCodeModal,
      config: { exportFinalLineage },
      node: mapAssetToNode(t),
    };

    ApiHelper.get = (async (url: string, data: Record<string, unknown>) => {
      console.log("ApiHelper V3", url, data);
      switch (url) {
        case "upstreamTables": {
          if (!("table" in data) || typeof data.table !== "string") {
            console.error("ApiHelper upstreamTables: expected data.table to be a string", data);
            return { tables: [] };
          }
          setShowLoader(true);
          const result = await upstreamTablesV2(data.table);
          setShowLoader(false);
          return {
            tables: (result?.assets ?? []).map(asset => mapAssetToNode(asset)),
          };
        }
        case "downstreamTables": {
          if (!("table" in data) || typeof data.table !== "string") {
            console.error("ApiHelper downstreamTables: expected data.table to be a string", data);
            return { tables: [] };
          }
          setShowLoader(true);
          const downstreamResult = await downstreamTablesV2(data.table);
          setShowLoader(false);
          return {
            tables: (downstreamResult?.assets ?? []).map(asset => mapAssetToNode(asset)),
          };
        }
        case "getColumns": {
          // For dashboard assets, we might not have columns
          if (data.asset_type === "dashboard") {
            return { purpose: "", columns: [] };
          }

          if (!("table" in data) || typeof data.table !== "string") {
            console.error("ApiHelper getColumns: expected data.table to be a string", data);
            return { purpose: "", columns: [] };
          }

          setShowLoader(true);
          const result = await postColumns({
            table_id: data.table,
          });
          setShowLoader(false);
          const { columns, purpose } = result || { columns: [], purpose: "" };
          return {
            purpose: purpose,
            columns: columns.map((c: LineageColumn) => ({
              ...c,
              name: c.name,
              table: columnRkToTableRk(c.rk),
              datatype: c.datatype,
              can_lineage_expand: c.can_lineage_expand,
              is_transformation: c.is_transformation,
              description: c.description,
            })),
          };
        }
        case "getDbtInfo": {
          if (!("table_rk" in data) || typeof data.table_rk !== "string") {
            console.error("ApiHelper getDbtInfo: expected data.table_rk to be a string", data);
            return;
          }
          setShowLoader(true);
          const result = await fetchDbtInfo({
            table_rk: data.table_rk,
          });
          setShowLoader(false);
          return result;
        }
        case "getConnectedColumns": {
          const { targets, currAnd1HopTables, upstreamExpansion, showIndirectEdges } = data;
          if (
            !Array.isArray(targets) ||
            !Array.isArray(currAnd1HopTables) ||
            typeof upstreamExpansion !== "boolean" ||
            typeof showIndirectEdges !== "boolean"
          ) {
            console.error("ApiHelper getConnectedColumns: unexpected data shape", data);
            return { column_lineage: [] };
          }
          const result = await postConnectedColumns({
            column_fqns: (targets as [string, string][]).map(t => `${t[0]}/${t[1]}`),
            nodes: currAnd1HopTables as string[],
            upstreamExpansion,
            showIndirectEdges,
          });
          const column_lineage: Record<string, unknown>[] = [];
          if (!result) return { column_lineage };

          const { highlight_edges } = result;
          for (const e of highlight_edges) {
            const type = e.extra?.type || "direct";
            if (type === "direct" && !edgeVisibility.current.showSelectEdges) continue;
            if (type === "indirect" && !edgeVisibility.current.showNonSelectEdges) continue;
            const lens_code = e.extra?.lens_code;
            const lens_type = e.extra?.lens_type;
            column_lineage.push({
              source: destructColumn(e["source"]),
              target: destructColumn(e["target"]),
              type: type,
              viewsType: lens_type,
              viewsCode: lens_code,
            });
          }

          return { column_lineage };
        }
        case "getLineageSettings":
          return {
            ...edgeVisibility.current,
            defaultExpansion: 1,
          };
        case "columnLineage": {
          if (data.event === "start") setShowLoader(true);
          else if (data.event === "end") setShowLoader(false);
          return;
        }
        case "init": {
          const event = new CustomEvent("renderStartNode", {
            detail: renderNode,
          });
          document.dispatchEvent(event);
          return;
        }
        case "persistLineageSettings": {
          edgeVisibility.current = { ...edgeVisibility.current, ...data };
          return;
        }
        case "showViewCodeModal": {
          const { table, column, datatype, viewsType, viewsCode, nodeType } = data;
          if (
            typeof table !== "string" ||
            typeof column !== "string" ||
            typeof datatype !== "string" ||
            typeof viewsType !== "string" ||
            typeof viewsCode !== "object" ||
            viewsCode === null ||
            typeof nodeType !== "string"
          ) {
            console.error("ApiHelper showViewCodeModal: unexpected data shape", data);
            return;
          }
          setViewCodeModalArgs?.({
            table,
            column,
            datatype,
            viewsType: viewsType as ViewsCodeModalArgs["viewsType"],
            viewsCode: viewsCode as ViewsCodeModalArgs["viewsCode"],
            nodeType,
          });
          return;
        }
        case "getSnowflakeStageDetails": {
          if (!("table_rk" in data) || typeof data.table_rk !== "string") {
            console.error(
              "ApiHelper getSnowflakeStageDetails: expected data.table_rk to be a string",
              data
            );
            return;
          }
          const result = await getSnowflakeStage({ table_rk: data.table_rk });
          return result;
        }
        case "finalColumnsDownload": {
          const { column_rk, upstream, downstream, direct, indirect } = data;
          if (
            typeof column_rk !== "string" ||
            typeof upstream !== "boolean" ||
            typeof downstream !== "boolean" ||
            typeof direct !== "boolean" ||
            typeof indirect !== "boolean"
          ) {
            console.error("ApiHelper finalColumnsDownload: unexpected data shape", data);
            return;
          }
          await getFinalLineageColumns(data as Parameters<typeof getFinalLineageColumns>[0]);
          return;
        }
        case "finalTablesDownload": {
          const { table_rk, upstream, downstream } = data;
          if (
            typeof table_rk !== "string" ||
            typeof upstream !== "boolean" ||
            typeof downstream !== "boolean"
          ) {
            console.error("ApiHelper finalTablesDownload: unexpected data shape", data);
            return;
          }
          await getFinalLineageTables(data as Parameters<typeof getFinalLineageTables>[0]);
          return;
        }
        case "getDashboardById": {
          if (!("dashboard_id" in data) || typeof data.dashboard_id !== "string") {
            console.error(
              "ApiHelper getDashboardById: expected data.dashboard_id to be a string",
              data
            );
            return;
          }
          setShowLoader(true);
          const result = await getBiDashboardById(data.dashboard_id);
          setShowLoader(false);
          return result;
        }
        case "getExposureDetails":
        case "sendFeedback":
        case "openFile":
        case "openChat":
        case "showInfoNotification":
        case "previewFeature":
        case "telemetryEvents":
        default:
          break;
      }
    }) as typeof ApiHelper.get;
    setIsApiHelperInitialized(true);
  }, [assetData, showViewCodeModal, exportFinalLineage, setViewCodeModalArgs]);

  return (
    <div className="al-relative al-h-[76vh]" aria-label="Lineage visualization">
      <Stack className="al-absolute al-right-4 al-z-[1]" direction="row" gap="md" align="baseline">
        <div id="settings-container" />
        <div id="reset-container" />
      </Stack>
      {(isLoading || showLoader) && <ComponentLoader label="loading..." />}
      {isApiHelperInitialized && (
        <UILineage
          theme={"light"}
          dynamicLineage={{ aiEnabled: true }}
          lineageType={"dynamic"}
          renderTags={renderTags}
          client="saas"
        />
      )}
    </div>
  );
};

export default AssetLineage;
